// src/services/imageProcessing.js

const DEFAULTS = {
  maxInputBytes: 25 * 1024 * 1024,   
  maxOutputBytes: 1_500_000,         
  maxPixels: 40_000_000,             
  maxDim: 1600,
  preferMime: 'image/jpeg',
  startQuality: 0.85,
  minQuality: 0.55,
};

function isHeic(file) {
  const t = (file?.type || '').toLowerCase();
  const n = (file?.name || '').toLowerCase();
  return t.includes('heic') || t.includes('heif') || n.endsWith('.heic') || n.endsWith('.heif');
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release() {
          bitmap.close?.();
        }
      };
    } catch (error) {
      // Some browsers expose createImageBitmap but still fail for valid uploads.
      // Falling back to an <img> decode makes the upload flow much more tolerant.
    }
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const img = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('IMG_LOAD_FAILED'));
      element.src = objectUrl;
    });

    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      release() {
        URL.revokeObjectURL(objectUrl);
      }
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

async function maybeConvertHeic(file) {
  if (!isHeic(file)) return file;

  try {
    const decoded = await decodeImage(file);
    decoded.release?.();
    return file;
  } catch (e) {
    const mod = await import('heic2any');
    const heic2any = mod.default || mod;
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    return new File([blob], (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg'), {
      type: 'image/jpeg'
    });
  }
}

async function canvasToBlob(canvas, mime, quality) {
  return await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), mime, quality);
  });
}

export async function processImageFile(inputFile, opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };
  if (!inputFile) throw new Error('NO_FILE');

  if (inputFile.size > cfg.maxInputBytes) {
    const err = new Error('INPUT_TOO_LARGE');
    err.code = 'INPUT_TOO_LARGE';
    err.maxBytes = cfg.maxInputBytes;
    throw err;
  }

  const file = await maybeConvertHeic(inputFile);

  let decoded;
  try {
    decoded = await decodeImage(file);
  } catch (e) {
    const err = new Error('DECODE_FAILED');
    err.code = 'DECODE_FAILED';
    throw err;
  }

  const w0 = decoded.width;
  const h0 = decoded.height;

  if (w0 * h0 > cfg.maxPixels) {
    decoded.release?.();
    const err = new Error('TOO_MANY_PIXELS');
    err.code = 'TOO_MANY_PIXELS';
    err.pixels = w0 * h0;
    err.maxPixels = cfg.maxPixels;
    throw err;
  }

  const needsResize = Math.max(w0, h0) > cfg.maxDim;
  const needsBytes = file.size > cfg.maxOutputBytes;

  const safeTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!needsResize && !needsBytes && safeTypes.includes((file.type || '').toLowerCase())) {
    decoded.release?.();
    return { file, width: w0, height: h0, compressed: false };
  }

  const scale = Math.min(1, cfg.maxDim / Math.max(w0, h0));
  let w = Math.max(1, Math.round(w0 * scale));
  let h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');

  if (!ctx) {
    decoded.release?.();
    const err = new Error('CANVAS_CONTEXT_FAILED');
    err.code = 'CANVAS_CONTEXT_FAILED';
    throw err;
  }

  canvas.width = w;
  canvas.height = h;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(decoded.source, 0, 0, w, h);

  decoded.release?.();

  let quality = cfg.startQuality;
  let blob = await canvasToBlob(canvas, cfg.preferMime, quality);

  while (blob.size > cfg.maxOutputBytes && quality > cfg.minQuality) {
    quality = Math.max(cfg.minQuality, quality - 0.08);
    blob = await canvasToBlob(canvas, cfg.preferMime, quality);
  }

  while (blob.size > cfg.maxOutputBytes && Math.max(w, h) > 900) {
    const previousCanvas = document.createElement('canvas');
    previousCanvas.width = canvas.width;
    previousCanvas.height = canvas.height;
    const previousCtx = previousCanvas.getContext('2d');

    if (!previousCtx) {
      const err = new Error('CANVAS_CONTEXT_FAILED');
      err.code = 'CANVAS_CONTEXT_FAILED';
      throw err;
    }

    previousCtx.drawImage(canvas, 0, 0);

    w = Math.max(1, Math.round(w * 0.85));
    h = Math.max(1, Math.round(h * 0.85));
    canvas.width = w;
    canvas.height = h;
    ctx = canvas.getContext('2d');
    if (!ctx) {
      const err = new Error('CANVAS_CONTEXT_FAILED');
      err.code = 'CANVAS_CONTEXT_FAILED';
      throw err;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(previousCanvas, 0, 0, w, h);
    quality = Math.max(cfg.minQuality, quality - 0.05);
    blob = await canvasToBlob(canvas, cfg.preferMime, quality);
  }

  if (blob.size > cfg.maxOutputBytes) {
    const err = new Error('OUTPUT_TOO_LARGE');
    err.code = 'OUTPUT_TOO_LARGE';
    err.bytes = blob.size;
    err.maxBytes = cfg.maxOutputBytes;
    throw err;
  }

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  const outName = `${baseName}.jpg`;
  const outFile = new File([blob], outName, { type: cfg.preferMime });

  return { file: outFile, width: w0, height: h0, compressed: true };
}
